import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, ReliefApplication, ReliefApplicationStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const STAFF_ROLES = ['RELIEF_ADMIN', 'DUTY_OFFICER', 'EOC_COMMANDER'];
const ADMIN_ROLES = ['RELIEF_ADMIN'];
const STATUSES: ReliefApplicationStatus[] = ['SUBMITTED', 'AUTO_ELIGIBLE', 'NEEDS_REVIEW', 'APPROVED', 'REJECTED', 'DISBURSED', 'CANCELLED'];

/** Post-event relief applications: auto-eligibility tiering from damage severity (DESTROYED/MAJOR fast-track), RELIEF_ADMIN decision + payout instruction (M36). */
export function ReliefApplications() {
  const { persona } = useAuth();
  const isStaff = persona ? persona.roles.some((r) => STAFF_ROLES.includes(r)) : false;
  const isAdmin = persona?.roles.includes('RELIEF_ADMIN') ?? false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m36/relief-applications?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<ReliefApplication>>(isStaff ? path : null);

  return (
    <div>
      <PageHeader
        title="Relief Applications"
        subtitle="Auto-eligibility from the linked damage assessment's severity — DESTROYED/MAJOR fast-track to AUTO_ELIGIBLE, MODERATE/MINOR need RELIEF_ADMIN review (M36)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New relief application
          </button>
        }
      />

      {!isStaff ? <RoleNotice roles={STAFF_ROLES} /> : null}
      {isStaff ? (
        <>
          <form
            className="mb-4 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setAppliedQ(q);
            }}
          >
            <input className="field-input !w-56" placeholder="Ref or applicant NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
            <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            <button type="submit" className="btn-primary">
              Search
            </button>
          </form>

          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No relief applications match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Applicant</th>
                    <th className="table-head">Tier</th>
                    <th className="table-head">Amount</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((r) => (
                    <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(r.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{r.refNo}</td>
                      <td className="table-cell font-mono text-xs">{r.applicantNin}</td>
                      <td className="table-cell text-xs">{r.eligibilityTier ?? '—'}</td>
                      <td className="table-cell text-xs tabular-nums">{scr(r.computedAmountScr)}</td>
                      <td className="table-cell">
                        <StatusBadge status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? (
        <DetailModal id={selectedId} isAdmin={isAdmin} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} />
      ) : null}
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DetailModal({ id, isAdmin, onClose, onChanged }: { id: string; isAdmin: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<ReliefApplication>(`/api/v1/m36/relief-applications/${id}`);
  const r = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [overrideAmount, setOverrideAmount] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={r ? r.refNo : 'Relief application'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {r ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={r.status} />} />
            <Detail label="Eligibility tier" value={r.eligibilityTier ?? '—'} />
            <Detail label="Computed amount" value={scr(r.computedAmountScr)} />
            <Detail label="Applicant" value={r.applicantNin} mono />
            <Detail label="Household size" value={String(r.householdSize)} />
            <Detail label="Bank / account" value={`${r.bankCode} · ${r.accountNo}`} mono />
          </div>
          {r.damageAssessment ? (
            <p className="text-xs text-slate-500">
              Linked assessment: <StatusBadge status={r.damageAssessment.structureType} /> <StatusBadge status={r.damageAssessment.severity} /> at {r.damageAssessment.address}
            </p>
          ) : null}
          {r.rejectionReason ? <ErrorNote message={`Rejected: ${r.rejectionReason}`} /> : null}
          {r.awardDocId ? <p className="text-xs text-seagreen-600">Award letter issued: {r.awardDocId}</p> : null}

          {r.disbursement ? (
            <div className="rounded-lg border border-slate-200 p-3 text-xs">
              <div className="mb-1 font-semibold text-slate-700">Disbursement</div>
              <StatusBadge status={r.disbursement.status} /> — {scr(r.disbursement.amountScr)} to {r.disbursement.accountNo}
              <p className="mt-1 text-slate-400">
                {r.disbursement.status === 'PENDING' || r.disbursement.status === 'QUEUED'
                  ? 'Payout confirmation arrives via the Treasury/F06 callback (service-token only) — no @onegov/core SDK client exists yet on that side, per the module build report.'
                  : null}
              </p>
            </div>
          ) : null}

          {isAdmin && (r.status === 'AUTO_ELIGIBLE' || r.status === 'NEEDS_REVIEW') ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Decision</div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Override amount (optional)">
                  <input className="field-input" type="number" placeholder={String(r.computedAmountScr ?? '')} value={overrideAmount} onChange={(e) => setOverrideAmount(e.target.value)} />
                </Field>
                <Field label="Review notes (optional)">
                  <input className="field-input" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} />
                </Field>
              </div>
              <button
                className="btn-success"
                disabled={busy}
                onClick={() =>
                  act(() =>
                    api.post(`/api/v1/m36/relief-applications/${id}/decision`, {
                      decision: 'APPROVE',
                      reviewNotes: reviewNotes || undefined,
                      overrideAmountScr: overrideAmount ? Number.parseFloat(overrideAmount) : undefined,
                    }),
                  )
                }
              >
                Approve (creates payout instruction + award letter)
              </button>
              <div className="flex gap-2">
                <input className="field-input" placeholder="Rejection reason" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
                <button
                  className="btn-danger"
                  disabled={busy || rejectionReason.trim().length < 3}
                  onClick={() => act(() => api.post(`/api/v1/m36/relief-applications/${id}/decision`, { decision: 'REJECT', rejectionReason }))}
                >
                  Reject
                </button>
              </div>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Submitted {formatDateTime(r.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [damageAssessmentId, setDamageAssessmentId] = useState('');
  const [applicantNin, setApplicantNin] = useState('');
  const [householdSize, setHouseholdSize] = useState('1');
  const [bankCode, setBankCode] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [accountName, setAccountName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m36/relief-applications', {
        damageAssessmentId,
        applicantNin: applicantNin || undefined,
        householdSize: Number.parseInt(householdSize, 10) || 1,
        bankCode,
        accountNo,
        accountName,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New relief application" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">
          Open a damage assessment from the Damage Assessments screen to copy its id here. A citizen with a verified NIN may only claim against their
          own assessment; RELIEF_ADMIN/EOC staff may specify a different applicantNin for counter intake.
        </p>
        <Field label="Damage assessment id">
          <input className="field-input" value={damageAssessmentId} onChange={(e) => setDamageAssessmentId(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant NIN (optional for staff intake)">
            <input className="field-input" value={applicantNin} onChange={(e) => setApplicantNin(e.target.value)} />
          </Field>
          <Field label="Household size">
            <input className="field-input" type="number" min="1" value={householdSize} onChange={(e) => setHouseholdSize(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Bank code">
            <input className="field-input" value={bankCode} onChange={(e) => setBankCode(e.target.value)} />
          </Field>
          <Field label="Account number">
            <input className="field-input" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} />
          </Field>
          <Field label="Account name">
            <input className="field-input" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !damageAssessmentId || !bankCode || !accountNo || !accountName} onClick={submit}>
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
